import {Collapse} from "@mui/material";
import {useTranslation} from "@i18n/client";
import React, {useEffect, useState} from "react";

import {markdown} from "../../../utils/markdown";
import Fade from "../../animations/Fade";
import {QA} from "../Faq/types";
import QuestionAnswer from "../Faq/QuestionAnswer";
import ShowMore from "./ShowMoreButton";
import {useTwMediaQuery} from "src/hooks/useTwMediaQuery";
import {Breakpoint} from "src/constants/breakpoint";

type Props = {
  title: string;
  description: string | null;
  faq: QA[];
  isInsuranceAccepted: boolean;
};

const ApptReasonFaq: React.FC<Props> = ({title, description, faq, isInsuranceAccepted}) => {
  const i18n = useTranslation();
  const isSm = !useTwMediaQuery(Breakpoint.LG);
  const [isExpanded, setIsExpanded] = useState(false);

  // For whatever reason, the markdown() library returns different things during client and server
  // rendering. On react v18 in strict mode, this causes a
  // https://nextjs.org/docs/messages/react-hydration-error.
  //
  // React then throws an "Uncaught Error: There was an error while hydrating. Because the error
  // happened outside of a Suspense boundary, the entire root will switch to client rendering."
  //
  // This useEffect() block defers rendering the markdown until the second render,
  // which resolves the error and gets _most_ of the page loading faster.
  const [desc, setDesc] = useState<string | JSX.Element | null>(description);
  useEffect(() => {
    if (typeof description === "string") {
      setDesc(markdown(description));
    }
  }, [description, setDesc]);

  useEffect(() => {
    setIsExpanded(!isSm);
  }, [isSm]);

  const shouldRenderShowMoreButton = isSm && faq && faq.length > 1;

  return (
    <div id="appt-reason-faq" className={shouldRenderShowMoreButton ? "" : "pb8"}>
      <h1 className="fs24-sm ls-25 pb2">{title}</h1>
      {!isInsuranceAccepted && (
        <strong className="font-cm fs28 fs20-md fs16-sm gray800 lh22 lh15-md">
          {i18n.t("Prices vary, insurance not accepted")}
        </strong>
      )}
      <div className="font-cm fw5 font-i-sm fw5-sm fs28 fs20-md fs16-sm ls-3 lh12 lh15-sm m-0 ">
        {desc}
      </div>

      <dl className="lg:mt-5 -mt-2">
        {faq?.map((qa, i) => {
          const isFirstFaq = i === 0;
          const isLastFaq = i === faq.length - 1;
          const hideBorder = (isFirstFaq && isSm && !isExpanded) || isLastFaq;
          return (
            <Collapse key={qa.q + i} in={isFirstFaq || isExpanded} unmountOnExit>
              <Fade in={i === 0 || isExpanded} appear>
                <QuestionAnswer
                  {...qa}
                  isPreview={!isExpanded && isSm && isFirstFaq}
                  hideIcon={!isExpanded && isSm && isFirstFaq}
                  hideBorder={hideBorder}
                  isExpandedOverride={isFirstFaq && isSm}
                  // @ts-expect-error TS2322: Type '(() => void) | null' is not assignable to type '(() => void) | undefined'.
                  onClick={
                    !isExpanded && isSm && isFirstFaq
                      ? () => {
                          setIsExpanded(!isExpanded);
                        }
                      : null
                  }
                />
              </Fade>
            </Collapse>
          );
        })}
        {shouldRenderShowMoreButton && (
          <ShowMore
            style={{marginTop: "1rem"}}
            text={isExpanded ? i18n.t("Show Less") : i18n.t("Show More")}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
            flipIcon={isExpanded}
          />
        )}
      </dl>
    </div>
  );
};

export default ApptReasonFaq;
